import { Children, type CSSProperties, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

/**
 * Form layout (V-LIB-FORMS, D508): `Field` (label, control, hint, error), `FormRow` (fields + actions on one row, the
 * action aligned to the CONTROL's bottom edge) and `FormGrid` (a responsive grid of fields).
 *
 * A field is three rows — label, control, message — and a `FormRow` / `FormGrid` lays those rows on a shared (sub)grid, so
 * a hint, an error or a two-line label on one field never moves another field's control, and an action button sits on the
 * control row. The old shape (a field with `margin-bottom` in a flex row with `align-items: flex-end`) put the button
 * beside the field BOX, 16–40px below the input.
 */

export interface FieldProps {
  readonly label: string;
  /** The `id` of the control this field labels (the control must carry it; the label's `for` points at it). */
  readonly htmlFor: string;
  /** The control. When it is a single element carrying `id={htmlFor}`, `Field` wires `aria-invalid`, `aria-required` and `aria-describedby` onto it. */
  readonly children: ReactNode;
  /** Help text under the control (`id` = `${htmlFor}-hint`). */
  readonly hint?: ReactNode;
  /** A refusal under the control (`id` = `${htmlFor}-error`, `role="alert"`); also sets `aria-invalid` on the control. */
  readonly error?: string;
  /** Marks the field required: a visual marker on the label and `aria-required` on the control (no native validation popup). */
  readonly required?: boolean;
  /** Extra content under hint / error (a live preview, a counter); put its id in the control's `aria-describedby` yourself. */
  readonly footer?: ReactNode;
  readonly className?: string;
  readonly style?: CSSProperties;
}

const joinIds = (...ids: readonly (string | undefined)[]): string | undefined => {
  const out = ids.filter((v): v is string => v !== undefined && v !== '').join(' ');
  return out === '' ? undefined : out;
};

export function Field({ label, htmlFor, children, hint, error, required = false, footer, className, style }: FieldProps): ReactElement {
  const hintId = `${htmlFor}-hint`;
  const errorId = `${htmlFor}-error`;
  const describedBy = joinIds(hint !== undefined ? hintId : undefined, error !== undefined ? errorId : undefined);
  let control: ReactNode = children;
  if (isValidElement<Record<string, unknown>>(children) && children.props['id'] === htmlFor) {
    const own = children.props['aria-describedby'];
    const wired: Record<string, unknown> = {};
    const merged = joinIds(typeof own === 'string' ? own : undefined, describedBy);
    if (merged !== undefined) wired['aria-describedby'] = merged;
    if (error !== undefined && children.props['aria-invalid'] === undefined) wired['aria-invalid'] = true;
    if (required && children.props['aria-required'] === undefined) wired['aria-required'] = true;
    control = cloneElement(children, wired);
  }
  return (
    <div className={`vello-field${className !== undefined ? ` ${className}` : ''}`} style={style} data-invalid={error !== undefined ? 'true' : undefined}>
      <label className="vello-field__label" htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="vello-field__req" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div className="vello-field__control">{control}</div>
      <div className="vello-field__msg">
        {hint !== undefined && (
          <span id={hintId} className="vello-field__hint">
            {hint}
          </span>
        )}
        {error !== undefined && (
          <span id={errorId} className="vello-field__error" role="alert">
            {error}
          </span>
        )}
        {footer}
      </div>
    </div>
  );
}

export interface FormRowProps {
  /** The fields — pass `Field`s as direct children (a wrapping fragment counts as one column). */
  readonly children: ReactNode;
  /** The action(s) at the end of the row: a button, or several. Aligned to the controls' bottom edge; full-width stacked under ~560px. */
  readonly action?: ReactNode;
  /** A CSS grid track list for the fields, e.g. `"2fr 1fr 1fr"` or `"minmax(0, 1fr) 9rem"`. Default: equal columns. */
  readonly columns?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

/**
 * Fields and actions in one row. Up to about four fields; use {@link FormGrid} for more. Under ~560px of its own width the
 * row stacks and the action goes full width.
 */
export function FormRow({ children, action, columns, className, style }: FormRowProps): ReactElement {
  const count = Math.max(1, Children.toArray(children).length);
  const fields = columns ?? `repeat(${count}, minmax(0, 1fr))`;
  const tracks = action !== undefined ? `${fields} max-content` : fields;
  return (
    <div className={`vello-formrow${className !== undefined ? ` ${className}` : ''}`} style={style}>
      <div className="vello-formrow__grid" style={{ '--vello-formrow-tracks': tracks } as CSSProperties}>
        {children}
        {action !== undefined && (
          <div className="vello-formrow__action">
            <span aria-hidden="true" />
            <div className="vello-formrow__action-control">{action}</div>
            <span aria-hidden="true" />
          </div>
        )}
      </div>
    </div>
  );
}

export interface FormGridProps {
  readonly children: ReactNode;
  /** The narrowest a field gets before the grid wraps: `sm` ~200px (default), `md` ~280px, `lg` ~360px. */
  readonly min?: 'sm' | 'md' | 'lg';
  readonly className?: string;
  readonly style?: CSSProperties;
}

/** A responsive grid of fields: as many equal columns as fit, each at least the `min` track wide. */
export function FormGrid({ children, min = 'sm', className, style }: FormGridProps): ReactElement {
  return (
    <div className={`vello-formgrid${min !== 'sm' ? ` vello-formgrid--${min}` : ''}${className !== undefined ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  );
}

/**
 * The language a native date input should write its day in (D854 DS-2, D845 L6c-F09 / L6b-015): the console's own language, which the
 * i18n kit puts on `<html lang>`. A browser formats `<input type="date">` by the `lang` of the element, so a pt-BR console reads
 * dd/mm/aaaa whatever the browser's UI language; with no document language the browser's own choice stands.
 */
export function dateInputLang(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const lang = document.documentElement.lang;
  return lang !== '' ? lang : undefined;
}

export interface DateRangeInputProps {
  /** Prefix of the two inputs' ids (`<idPrefix>-from`, `<idPrefix>-to`). */
  readonly idPrefix: string;
  /** `YYYY-MM-DD` (the native date input's value). */
  readonly from: string;
  readonly to: string;
  readonly onFromChange: (day: string) => void;
  readonly onToChange: (day: string) => void;
  /** The inputs' accessible names ("From", "To"). */
  readonly fromLabel: string;
  readonly toLabel: string;
  /** Draws both inputs with the danger edge and sets `aria-invalid`. */
  readonly invalid?: boolean;
}

/**
 * A from–to day range that sits on a filter row at the controls' 48px height (owner, 2026-10-12: the dates are always on
 * screen beside the quick presets — no "custom" step, no label stacked above that pushes them off the row's line). Two
 * native date inputs sized to their content, a dash between them; each input carries its own accessible name.
 */
export function DateRangeInput({ idPrefix, from, to, onFromChange, onToChange, fromLabel, toLabel, invalid = false }: DateRangeInputProps): ReactElement {
  const lang = dateInputLang();
  return (
    <div className="vello-daterange">
      <input id={`${idPrefix}-from`} type="date" lang={lang} className="vello-input vello-daterange__input" aria-label={fromLabel} aria-invalid={invalid ? true : undefined} value={from} onChange={(e) => onFromChange(e.target.value)} />
      <span className="vello-daterange__sep" aria-hidden="true">–</span>
      <input id={`${idPrefix}-to`} type="date" lang={lang} className="vello-input vello-daterange__input" aria-label={toLabel} aria-invalid={invalid ? true : undefined} value={to} onChange={(e) => onToChange(e.target.value)} />
    </div>
  );
}
